import React, { useEffect, useState } from 'react';
import InternalLayout from '../../components/InternalLayout';
import { internalApi } from '../../utils/api';

// PRD-008 §7 / AC-6 — single-column scannable layout. Eight metrics
// laid out as a large number with a small inline 30-day sparkline
// beside it and a one-line label below. AI Assist spend is the most
// prominent because it changes daily and has a hard cap.

function Sparkline({ values }) {
  if (!values || values.length === 0) return <svg width="120" height="32" />;
  const max = Math.max(1, ...values.map((v) => v.value || 0));
  const w = 120;
  const h = 32;
  const step = w / Math.max(1, values.length - 1);
  const points = values
    .map((v, i) => `${(i * step).toFixed(1)},${(h - (v.value / max) * h).toFixed(1)}`)
    .join(' ');
  return (
    <svg width={w} height={h} className="text-slate-500">
      <polyline points={points} fill="none" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function StatusDot({ status }) {
  const color =
    status === 'red' ? 'bg-red-500'
      : status === 'amber' ? 'bg-amber-500'
        : 'bg-emerald-500';
  return <span className={`inline-block w-2 h-2 rounded-full mr-2 ${color}`} />;
}

function MetricRow({ children }) {
  return (
    <div className="flex items-start justify-between border-b border-gray-100 py-4">
      {children}
    </div>
  );
}

export default function OperatorDashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = async (force) => {
    try {
      const res = await internalApi.dashboard(force ? { force: 'true' } : {});
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };
  useEffect(() => { load(false); }, []);

  return (
    <InternalLayout pageTitle="Platform KPIs">
      {error && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-800">{error}</div>
      )}

      <div className="bg-white rounded-md border border-gray-200 shadow-sm divide-y">
        {!data && <div className="p-6 text-gray-600 text-sm">Loading…</div>}
        {data && data.metrics.map((m) => (
          <div key={m.metric} className="px-5 py-4">
            <MetricRow>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-3">
                  {m.metric === 'ai_assist_anthropic_spend' && (
                    <>
                      <StatusDot status={m.status} />
                      <span className="text-3xl font-semibold text-gray-900">
                        ${m.today_usd.toFixed(2)}
                      </span>
                      <span className="text-sm text-gray-600">today</span>
                      <span className="text-sm text-gray-600">/ ${m.daily_cap_usd} cap</span>
                      <span className="text-sm text-gray-600">· ${m.month_to_date_usd.toFixed(2)} MTD</span>
                    </>
                  )}
                  {m.metric === 'active_workspaces' && (
                    <span className="text-3xl font-semibold text-gray-900">{m.current_value}</span>
                  )}
                  {m.metric === 'connections_by_state' && (
                    <>
                      <span className="text-3xl font-semibold text-gray-900">{m.breakdown.active}</span>
                      <span className="text-sm text-gray-600">active</span>
                      <span className="text-sm text-gray-600">·</span>
                      <span className="text-sm text-gray-700">{m.breakdown.pending} pending</span>
                      <span className="text-sm text-gray-600">·</span>
                      <span className="text-sm text-gray-700">{m.breakdown.severed} severed</span>
                    </>
                  )}
                  {m.metric === 'proposal_funnel' && (
                    <>
                      <span className="text-3xl font-semibold text-gray-900">{m.funnel.signed}</span>
                      <span className="text-sm text-gray-600">signed</span>
                      <span className="text-sm text-gray-600">·</span>
                      <span className="text-sm text-gray-700">{m.funnel.shared} shared</span>
                      <span className="text-sm text-gray-600">·</span>
                      <span className="text-sm text-gray-700">{m.funnel.drafted} drafted</span>
                    </>
                  )}
                  {m.metric === 'active_agreements_and_gmv' && (
                    <>
                      <span className="text-3xl font-semibold text-gray-900">${Number(m.cumulative_gmv_usd).toLocaleString()}</span>
                      <span className="text-sm text-gray-600">cumulative GMV · {m.active_agreements} active</span>
                    </>
                  )}
                  {m.metric === 'take_rate_realized_mtd' && (
                    <>
                      <span className="text-3xl font-semibold text-gray-900">${m.fee_usd_mtd.toFixed(2)}</span>
                      <span className="text-sm text-gray-600">take rate MTD ({m.take_rate_pct}%)</span>
                    </>
                  )}
                  {m.metric === 'blended_portfolio_gross_margin' && (
                    <>
                      <span className="text-3xl font-semibold text-gray-900">
                        {m.current_value === null ? '—' : `${m.current_value.toFixed(2)}%`}
                      </span>
                      <span className="text-sm text-gray-600">blended margin</span>
                    </>
                  )}
                  {m.metric === 'model_pricing_catalog_freshness' && (
                    <>
                      <span className="text-3xl font-semibold text-gray-900">
                        {m.last_synced_at ? new Date(m.last_synced_at).toLocaleString() : '—'}
                      </span>
                      <span className="text-sm text-gray-600">last sync · {m.drift_count} drifted</span>
                    </>
                  )}
                </div>
                <div className="text-sm text-gray-600 mt-1">{m.label}</div>
                {m.note && <div className="text-xs text-gray-600 italic mt-1">{m.note}</div>}
              </div>
              <Sparkline values={m.sparkline_30_day} />
            </MetricRow>
          </div>
        ))}
      </div>
      {data && (
        <div className="mt-3 flex items-center gap-3 text-xs text-gray-600">
          <span>Generated {new Date(data.generated_at).toLocaleString()}</span>
          {data.served_from_cache && <span>· served from cache</span>}
          <button onClick={() => load(true)} className="text-slate-700 hover:underline">Force refresh</button>
        </div>
      )}
    </InternalLayout>
  );
}
